// The Routines list's two filter axes — **Space, then Agent** — and the pure logic behind them, the same
// pair the Tasks list offers (the category pages draw one filter row for every category, Figma 21055:248873).
//
// Two conventions, each preventing a defect this screen would otherwise have:
//
//   1. **Options are DERIVED from the routines in scope**, never from the full Space roster — a menu must
//      not contain a value that matches nothing.
//   2. **A control with nothing to offer is not rendered at all** (`offersAChoice` is the older, stricter
//      "two or more" rule; the category filter row draws a menu as soon as there is one option).
//
// The state is ids, not names: names are display, and two Spaces can be renamed to the same thing.
//
// ⚠️ An **agent scope** is a different thing from the agent FILTER: arriving at the list from an agent fixes
// `agentId` on `RoutinesBody`, which narrows before this module sees the rows, and the host then offers no
// agent menu at all. That is the host deciding what the list is, not a control the reader can work.
//
// Mirrored 1:1 by `RoutineFilters.swift` and `RoutineFilters.kt`; all three unit-test the same fixtures.

/** A routine, as far as filtering is concerned. */
export interface FilterableRoutine {
  workspaceId?: string;
  agentId?: string;
  agentName?: string | null;
}

/** Something a filter can offer: an id plus the name to show for it. */
export interface FilterOption {
  id: string;
  name: string;
}

/** Which Space the list is narrowed to. `''` means "all". */
export interface RoutineFilterState {
  workspaceId: string;
  agentId: string;
}

export const NO_ROUTINE_FILTERS: RoutineFilterState = { workspaceId: '', agentId: '' };

/** Whether anything is narrowed — drives the Clear affordance. */
export function hasActiveRoutineFilters(filters: RoutineFilterState): boolean {
  return activeRoutineFilterCount(filters) > 0;
}

/** How many axes are narrowed — the N in "Clear filters · N". */
export function activeRoutineFilterCount(filters: RoutineFilterState): number {
  return (filters.workspaceId ? 1 : 0) + (filters.agentId ? 1 : 0);
}

/**
 * The Spaces worth offering: those that actually own a routine here, in the roster's own order so the
 * picker reads like the sidebar rather than a shifting most-recent list. A Space the roster hasn't
 * resolved yet is skipped rather than shown by id.
 *
 * The site-level brief routine carries an empty `workspaceId` on purpose (it belongs to every Space), so
 * it contributes nothing here — it is never the reason a Space appears in the menu.
 */
export function spacesOf(routines: FilterableRoutine[], workspaces: FilterOption[]): FilterOption[] {
  const present = new Set(routines.map((routine) => routine.workspaceId).filter(Boolean));
  return workspaces.filter((workspace) => present.has(workspace.id));
}

/**
 * Whether a picker earns its place. One option is not a choice — it names the only thing there is, which
 * the list already says — so a control appears only once there are two things to pick between.
 */
export function offersAChoice(options: FilterOption[]): boolean {
  return options.length > 1;
}

/**
 * Narrow the list. An axis set to `''` does not narrow.
 *
 * A routine with **no** `workspaceId` is site-level (the morning brief) and belongs to every Space, so it
 * survives a Space filter rather than being hidden by one — the same rule the server applies when it
 * merges the brief into a workspace-scoped page.
 */
/**
 * The agents worth offering: those that actually run a routine here, in the team roster's order. The name
 * comes from the roster, else from the routine itself, so an agent the roster has not resolved yet is
 * still named rather than dropped.
 */
export function agentsOf(routines: FilterableRoutine[], team: FilterOption[]): FilterOption[] {
  const named = new Map<string, string>();
  for (const routine of routines) {
    if (routine.agentId && !named.has(routine.agentId)) named.set(routine.agentId, routine.agentName?.trim() ?? '');
  }
  const fromTeam = team.filter((agent) => named.has(agent.id));
  const rest = [...named]
    .filter(([id, name]) => name && !fromTeam.some((agent) => agent.id === id))
    .map(([id, name]) => ({ id, name }));
  return [...fromTeam, ...rest];
}

export function filterRoutines<T extends FilterableRoutine>(routines: T[], filters: RoutineFilterState): T[] {
  return routines.filter(
    (routine) =>
      (!filters.workspaceId || !routine.workspaceId || routine.workspaceId === filters.workspaceId) &&
      (!filters.agentId || routine.agentId === filters.agentId),
  );
}

/**
 * Drop a selection that the routines in scope no longer support, so a filter can never strand the list on
 * an empty screen with no visible cause.
 */
export function clampRoutineFilters(filters: RoutineFilterState, spaces: FilterOption[], agents: FilterOption[] = []): RoutineFilterState {
  return {
    workspaceId: spaces.some((space) => space.id === filters.workspaceId) ? filters.workspaceId : '',
    agentId: agents.some((agent) => agent.id === filters.agentId) ? filters.agentId : '',
  };
}
